/**
 * Controller Telemetry Service
 * 
 * Tracks controller usage and performance metrics for A/B testing and analysis.
 */

export interface ControllerMetrics {
  schemeName: string
  sessionId: string
  startTime: number
  endTime?: number
  totalActions: number
  actionCounts: Map<string, number>
  averageActionsPerMinute: number
  sessionDuration: number
}

export interface ActionTelemetry {
  action: string
  timestamp: number
  schemeName: string
  sessionId: string
}

export class ControllerTelemetryService {
  private static instance: ControllerTelemetryService | null = null
  private currentSession: ControllerMetrics | null = null
  private sessionHistory: ControllerMetrics[] = []
  private maxHistorySize: number = 100
  private readonly STORAGE_KEY = 'controller-telemetry'

  private constructor() {
    this.loadHistory()
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): ControllerTelemetryService {
    if (!ControllerTelemetryService.instance) {
      ControllerTelemetryService.instance = new ControllerTelemetryService()
    }
    return ControllerTelemetryService.instance
  }

  /**
   * Start a new session
   */
  public startSession(schemeName: string): void {
    // End current session if exists
    if (this.currentSession) {
      this.endSession()
    }

    this.currentSession = {
      schemeName,
      sessionId: this.generateSessionId(),
      startTime: Date.now(),
      totalActions: 0,
      actionCounts: new Map(),
      averageActionsPerMinute: 0,
      sessionDuration: 0
    }
  }

  /**
   * End current session
   */
  public endSession(): void {
    if (!this.currentSession) return

    this.currentSession.endTime = Date.now()
    this.currentSession.sessionDuration = this.currentSession.endTime - this.currentSession.startTime

    // Calculate average actions per minute
    const minutes = this.currentSession.sessionDuration / (1000 * 60)
    this.currentSession.averageActionsPerMinute = minutes > 0
      ? this.currentSession.totalActions / minutes
      : 0

    // Add to history
    this.sessionHistory.push({
      ...this.currentSession,
      actionCounts: new Map(this.currentSession.actionCounts)
    })

    // Trim history if needed
    if (this.sessionHistory.length > this.maxHistorySize) {
      this.sessionHistory = this.sessionHistory.slice(-this.maxHistorySize)
    }

    this.saveHistory()
    this.currentSession = null
  }

  /**
   * Record an action
   */
  public recordAction(action: string): void {
    if (!this.currentSession) return

    this.currentSession.totalActions++

    const count = this.currentSession.actionCounts.get(action) || 0
    this.currentSession.actionCounts.set(action, count + 1)
  }

  /**
   * Get current session metrics
   */
  public getCurrentSession(): ControllerMetrics | null {
    return this.currentSession
  }

  /**
   * Get session history
   */
  public getSessionHistory(): ControllerMetrics[] {
    return [...this.sessionHistory]
  }

  /**
   * Get aggregated metrics by scheme
   */
  public getMetricsByScheme(schemeName: string): {
    totalSessions: number
    totalActions: number
    averageActionsPerSession: number
    averageSessionDuration: number
    mostUsedActions: [string, number][]
  } {
    const sessions = this.sessionHistory.filter(s => s.schemeName === schemeName)

    if (sessions.length === 0) {
      return {
        totalSessions: 0,
        totalActions: 0,
        averageActionsPerSession: 0,
        averageSessionDuration: 0,
        mostUsedActions: []
      }
    }

    const totalActions = sessions.reduce((sum, s) => sum + s.totalActions, 0)
    const totalDuration = sessions.reduce((sum, s) => sum + s.sessionDuration, 0)

    // Aggregate action counts
    const actionCounts = new Map<string, number>()
    sessions.forEach(session => {
      session.actionCounts.forEach((count, action) => {
        const currentCount = actionCounts.get(action) || 0
        actionCounts.set(action, currentCount + count)
      })
    })

    // Sort actions by count
    const sortedActions = Array.from(actionCounts.entries())
      .sort((a, b) => b[1] - a[1])

    return {
      totalSessions: sessions.length,
      totalActions,
      averageActionsPerSession: totalActions / sessions.length,
      averageSessionDuration: totalDuration / sessions.length,
      mostUsedActions: sortedActions
    }
  }

  /**
   * Get all scheme names that have been used
   */
  public getUsedSchemes(): string[] {
    const schemes = new Set<string>()
    this.sessionHistory.forEach(s => schemes.add(s.schemeName))
    return Array.from(schemes)
  }

  /**
   * Clear all telemetry data
   */
  public clearData(): void {
    this.currentSession = null
    this.sessionHistory = []
    this.saveHistory()
  }

  /**
   * Generate a unique session ID
   * Note: Uses Math.random() which is sufficient for telemetry session IDs.
   * This is not used for security-sensitive purposes.
   */
  private generateSessionId(): string {
    // Use timestamp + random for uniqueness (not cryptographically secure but sufficient for telemetry)
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Save history to localStorage
   */
  private saveHistory(): void {
    try {
      // Convert Map to array for JSON serialization
      const serializable = this.sessionHistory.map(session => ({
        ...session,
        actionCounts: Array.from(session.actionCounts.entries())
      }))

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(serializable))
    } catch (error) {
      console.warn('ControllerTelemetryService: Failed to save history:', error)
    }
  }

  /**
   * Load history from localStorage
   */
  private loadHistory(): void {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)

        // Convert array back to Map
        this.sessionHistory = parsed.map((session: any) => ({
          ...session,
          actionCounts: new Map(session.actionCounts)
        }))
      }
    } catch (error) {
      console.warn('ControllerTelemetryService: Failed to load history:', error)
      this.sessionHistory = []
    }
  }

  /**
   * Export telemetry data as JSON
   */
  public exportData(): string {
    const data = {
      currentSession: this.currentSession ? {
        ...this.currentSession,
        actionCounts: Array.from(this.currentSession.actionCounts.entries())
      } : null,
      sessionHistory: this.sessionHistory.map(session => ({
        ...session,
        actionCounts: Array.from(session.actionCounts.entries())
      })),
      summary: this.getUsedSchemes().map(scheme => ({
        scheme,
        metrics: this.getMetricsByScheme(scheme)
      }))
    }

    return JSON.stringify(data, null, 2)
  }
}
